import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export const useUniqueArticleHistory = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /unique-articles/:id/history']>();

  return useQuery({
    queryKey: ['unique-article-history', id],
    queryFn: () => fetch(`/unique-articles/${id}/history`),
    enabled: !!id,
  });
};

export const useUniqueArticles = (
  params: Endpoints['GET /unique-articles']['query'],
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /unique-articles']>();

  return useQuery({
    queryKey: ['unique-articles', params],
    queryFn: () => fetch('/unique-articles', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useAuthenticityCertificate = (uniqueArticleId?: string) => {
  const fetch = useFetch<Endpoints['GET /unique-articles/:id/authenticity-certificate']>();

  return useQuery({
    queryKey: ['unique-articles', uniqueArticleId, 'authenticity-certificate'],
    queryFn: () => fetch(`/unique-articles/${uniqueArticleId}/authenticity-certificate`),
    enabled: !!uniqueArticleId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useCreateUniqueArticle = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /unique-articles']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /unique-articles']['body']) =>
      fetch(`/unique-articles`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
  });
};
